import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { BookOpen, Users, Radio, Flower2, Eye, EyeOff, Mail } from 'lucide-react';
import { useAudio } from '@/contexts/AudioContext';

const HomePage = ({ onLogin }) => {
  const location = useLocation();
  const [showAuth, setShowAuth] = useState(false);
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const { fetchAndPlayMusic } = useAudio();
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [showResendVerification, setShowResendVerification] = useState(false);
  const [pendingEmail, setPendingEmail] = useState(null);       // shown after registering
  const [emailSendFailed, setEmailSendFailed] = useState(false);
  const [resendEmail, setResendEmail] = useState('');
  
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [registerForm, setRegisterForm] = useState({
    username: '',
    email: '',
    password: '',
    full_name: '',
    accept_terms: false,
    accept_privacy: false,
    accept_guidelines: false
  });

  // Auto-play music on HomePage load (will only work after user interaction due to browser restrictions)
  useEffect(() => {
    // Attempt to fetch and play music, but it will be blocked until user clicks
    fetchAndPlayMusic('home');
    
    // Add a click listener to the entire page to start music on first interaction
    const handleFirstClick = () => {
      fetchAndPlayMusic('home');
      // Remove listener after first click
      document.removeEventListener('click', handleFirstClick);
    };
    
    document.addEventListener('click', handleFirstClick);
    
    return () => {
      document.removeEventListener('click', handleFirstClick);
    };
  }, []);
  
  // Show verification success message if redirected from verification page
  useEffect(() => {
    if (location.state?.verified) {
      toast.success(location.state.message);
      // Clear the state
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/login`, loginForm);
      onLogin(response.data.token, response.data.user);
      toast.success('Welcome back!');
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Login failed';
      
      // Check if it's an email verification error
      if (errorMessage.includes('verify your email')) {
        setShowResendVerification(true);
        setResendEmail(loginForm.email);
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };
  
  const handleResendVerification = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/auth/resend-verification`, { email: resendEmail });
      toast.success('Verification email sent! Please check your inbox.');
      setShowResendVerification(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend verification email');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = () => {
    const redirectUri = `${window.location.origin}/auth/callback`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUri)}`;
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/register`, registerForm);
      const email = response.data.email || registerForm.email;

      // Show the "check your email" step instead of leaving people on an empty form
      setPendingEmail(email);
      setEmailSendFailed(response.data.email_sent === false);
      setResendEmail(email);
      setRegisterForm({
        username: '',
        email: '',
        password: '',
        full_name: '',
        accept_terms: false,
        accept_privacy: false,
        accept_guidelines: false
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const resendToPending = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/auth/resend-verification`, { email: pendingEmail });
      setEmailSendFailed(false);
      toast.success('Sent again — check your inbox, and your spam folder just in case.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not send the email. Please try again shortly.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Outer Space Background - Scorpio Constellation */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ 
          backgroundImage: 'url(https://images.unsplash.com/photo-1679615845580-8691c78fd7d3?q=80&w=2000)',
          zIndex: 0
        }}
      ></div>
      
      {/* Colored Overlay with Reduced Opacity */}
      <div 
        className="absolute inset-0" 
        style={{ 
          background: 'linear-gradient(to bottom, rgba(10, 14, 26, 0.5) 0%, rgba(26, 35, 50, 0.6) 50%, rgba(45, 55, 72, 0.7) 100%)',
          zIndex: 1
        }}
      ></div>
      
      {/* Subtle glow effects */}
      <div className="absolute top-20 left-20 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" style={{ zIndex: 2 }}></div>
      <div className="absolute bottom-20 right-20 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" style={{ zIndex: 2 }}></div>
      
      <div className="relative" style={{ zIndex: 10 }}>
        {/* Hero Section */}
        {!showAuth ? (
          <div className="container mx-auto px-6 py-20">
            <div className="flex flex-col items-center text-center space-y-8 content-animate">
              {/* Logo */}
              <div className="mb-8 w-64 h-64 rounded-full overflow-hidden mx-auto">
                <img 
                  src="https://customer-assets.emergentagent.com/job_eleven-collective/artifacts/56npyh3g_11-11%20Coaching%20App.jpg" 
                  alt="11/11 Collective Connection" 
                  className="w-full h-full object-contain spiritual-glow"
                />
              </div>
              
              {/* Main Heading */}
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-tight">
                Activate Your Potential<br />
                <span className="text-cyan-400">Embody Your Authentic Self</span>
              </h1>
              
              <p className="text-lg sm:text-xl text-gray-300 max-w-2xl">
                A multidimensional growth experience designed to help you live from expanded awareness
              </p>
              
              <div className="flex gap-4 mt-8">
                <Button 
                  data-testid="get-started-btn"
                  onClick={() => setShowAuth(true)} 
                  className="btn-primary flex flex-col items-center leading-tight h-auto px-10 py-4"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  <span className="text-xl font-semibold">Enter the Portal</span>
                  <span className="text-xl font-semibold">Empowerment Awaits</span>
                </Button>
              </div>

              {/* Features — 6 pillars, 2 rows × 3, flip on hover/tap */}
              <style>{`
                .pillar-grid { perspective: 1200px; }
                .pillar-flip { height: 230px; outline: none; cursor: pointer; }
                .pillar-inner { position: relative; width: 100%; height: 100%; transition: transform 0.6s cubic-bezier(.4,.2,.2,1); transform-style: preserve-3d; }
                .pillar-flip:hover .pillar-inner,
                .pillar-flip:focus .pillar-inner,
                .pillar-flip:focus-within .pillar-inner { transform: rotateY(180deg); }
                .pillar-face { position: absolute; inset: 0; -webkit-backface-visibility: hidden; backface-visibility: hidden; border-radius: 0.75rem; border: 1px solid rgba(6,182,212,0.3); background-color: #e6f8fe; display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; padding: 1.5rem; box-shadow: 0 10px 30px -12px rgba(51,26,103,0.25); }
                .pillar-back { transform: rotateY(180deg); }
              `}</style>
              <div className="pillar-grid grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-20 w-full max-w-6xl">
                {[
                  { title: 'Self-Paced Program', icon: 'https://customer-assets.emergentagent.com/job_eleven-collective/artifacts/fs0qshtp_Expert%20Icon.png', desc: 'Your full transformational curriculum delivered through guided modules, teachings, practices, and multidimensional tools — supporting your healing, growth, and expansion. Guidance from over twenty collaborators through approximately nine months of exploring spiritual consciousness.' },
                  { title: 'Workshops & Teachings', icon: 'https://customer-assets.emergentagent.com/job_eleven-collective/artifacts/oyrn7dck_Guided%20Modules%20Icon.png', desc: 'Additional workshops from the practitioners, collaborators, and experts of the coaching program — expanding your knowledge, skills, and spiritual development.' },
                  { title: 'Community & Chat', icon: 'https://customer-assets.emergentagent.com/job_eleven-collective/artifacts/cffjgr8a_Community%20Icon.png', desc: 'Connect, share, and grow with others through conversations, experiences, and collective support — strengthening your healing, growth, and expansion. In addition, you get direct access to the practitioners, experts and collaborators inside the coaching program.' },
                  { title: 'Session Booking', icon: '/icons/sessions-331a67.png', blend: true, desc: 'Schedule personalized sessions with practitioners and experts to receive direct guidance and support for your healing, growth, and expansion.' },
                  { title: 'Live Streams & Events', icon: 'https://customer-assets.emergentagent.com/job_eleven-collective/artifacts/zbda8j21_Live%20Session%20Icon.png', desc: 'Real-time teachings, workshops, energetic work, and community gatherings — deepening your healing, growth, and expansion.' },
                  { title: 'Marketplace', icon: '/icons/marketplace-331a67.png', blend: true, desc: 'Products, tools, literature, and decorations to support your healing, growth, and multidimensional expansion.' },
                ].map((p) => (
                  <div
                    key={p.title}
                    className="pillar-flip"
                    tabIndex={0}
                    role="button"
                    aria-label={p.title}
                    data-testid={`pillar-${p.title.toLowerCase().replace(/[^a-z]+/g, '-').replace(/^-|-$/g, '')}`}
                  >
                    <div className="pillar-inner">
                      {/* Front: icon + title */}
                      <div className="pillar-face">
                        <div className="w-28 h-28 mb-4">
                          <img
                            src={p.icon}
                            alt={p.title}
                            className="w-full h-full object-contain"
                            style={p.blend
                              ? { transform: 'scale(1.55)' }
                              : { filter: 'brightness(0) saturate(100%) invert(12%) sepia(48%) saturate(4588%) hue-rotate(255deg) brightness(91%) contrast(92%)' }}
                          />
                        </div>
                        <h3 className="text-xl font-semibold" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#331a67' }}>{p.title}</h3>
                      </div>
                      {/* Back: description */}
                      <div className="pillar-face pillar-back">
                        <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#331a67' }}>{p.title}</h3>
                        <p className="text-sm leading-snug" style={{ color: '#445277' }}>{p.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="container mx-auto px-6 py-20">
            <div className="max-w-md mx-auto content-animate">
              <Card className="glass-effect border-cyan-500/30">
                <CardContent className="p-8">
                  <div className="text-center mb-6">
                    <div className="w-32 h-32 rounded-full overflow-hidden mx-auto mb-4">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_eleven-collective/artifacts/56npyh3g_11-11%20Coaching%20App.jpg" 
                        alt="Logo" 
                        className="w-full h-full object-contain spiritual-glow"
                      />
                    </div>
                    <h2 className="text-3xl font-bold" style={{ color: '#2dd4bf' }}>Welcome</h2>
                  </div>
                  
                  {pendingEmail ? (
                    <div className="space-y-5 text-center" data-testid="register-check-email">
                      <div className="w-14 h-14 rounded-full bg-cyan-500/15 border border-cyan-400/40 flex items-center justify-center mx-auto">
                        <Mail className="w-7 h-7" style={{ color: '#2dd4bf' }} />
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold" style={{ color: '#2dd4bf' }}>
                          One more step — check your email
                        </h3>
                        <p className="text-sm mt-2 text-gray-700">
                          We sent a confirmation link to
                        </p>
                        <p className="text-sm font-medium text-gray-900 break-all" data-testid="register-pending-email">
                          {pendingEmail}
                        </p>
                        <p className="text-sm mt-3 text-gray-600 leading-relaxed">
                          Open it and tap <strong>Verify Email</strong>. That switches your account on,
                          and then you can log in here. The link works on any device — phone or computer.
                        </p>
                      </div>

                      {emailSendFailed && (
                        <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2">
                          We had trouble sending that email. Try &quot;Send it again&quot; below.
                        </p>
                      )}

                      <p className="text-xs text-gray-500">
                        Nothing after a minute or two? Check your spam or promotions folder.
                      </p>

                      <div className="space-y-2">
                        <Button
                          type="button"
                          onClick={resendToPending}
                          variant="outline"
                          className="w-full"
                          disabled={loading}
                          data-testid="register-resend-btn"
                        >
                          {loading ? 'Sending…' : 'Send it again'}
                        </Button>
                        <Button
                          type="button"
                          onClick={() => { setPendingEmail(null); setIsLogin(true); }}
                          className="w-full btn-primary"
                          data-testid="register-go-to-login"
                        >
                          I&apos;ve verified — take me to login
                        </Button>
                      </div>
                    </div>
                  ) : (
                  <Tabs value={isLogin ? 'login' : 'register'} onValueChange={(v) => setIsLogin(v === 'login')}>
                    <TabsList className="grid w-full grid-cols-2 mb-6">
                      <TabsTrigger value="login" data-testid="login-tab" style={{ color: '#2dd4bf' }}>Login</TabsTrigger>
                      <TabsTrigger value="register" data-testid="register-tab">Register</TabsTrigger>
                    </TabsList>
                    
                    <TabsContent value="login">
                      <form onSubmit={handleLogin} className="space-y-4">
                        <div>
                          <Input
                            data-testid="login-email"
                            id="login-email"
                            name="email"
                            type="email"
                            autoComplete="username"
                            inputMode="email"
                            placeholder="Email"
                            value={loginForm.email}
                            onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                            required
                            className="bg-white border-cyan-500/30 placeholder:text-gray-400"
                            style={{ color: '#000000' }}
                          />
                        </div>
                        <div className="relative">
                          <Input
                            data-testid="login-password"
                            id="login-password"
                            name="password"
                            type={showLoginPassword ? "text" : "password"}
                            autoComplete="current-password"
                            placeholder="Password"
                            value={loginForm.password}
                            onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                            required
                            className="bg-white border-cyan-500/30 placeholder:text-gray-400 pr-10"
                            style={{ color: '#000000' }}
                          />
                          <button
                            type="button"
                            onClick={() => setShowLoginPassword(!showLoginPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-800"
                          >
                            {showLoginPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                        <Button data-testid="login-submit-btn" type="submit" className="w-full btn-primary" disabled={loading}>
                          {loading ? 'Logging in...' : 'Login'}
                        </Button>
                        
                        <div className="text-center space-y-2">
                          <a 
                            href="/forgot-password" 
                            className="text-sm hover:underline block"
                            style={{ color: '#2dd4bf' }}
                          >
                            Forgot Password?
                          </a>
                        </div>

                        {/* Divider */}
                        <div className="relative my-2">
                          <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-gray-300" />
                          </div>
                          <div className="relative flex justify-center text-xs">
                            <span className="px-2 text-gray-500 bg-white">or</span>
                          </div>
                        </div>

                        {/* Google Sign-In */}
                        <button
                          type="button"
                          onClick={handleGoogleSignIn}
                          data-testid="google-signin-btn"
                          className="w-full flex items-center justify-center gap-3 px-4 py-2.5 rounded-md border border-gray-300 hover:border-gray-400 bg-white hover:bg-gray-50 transition-all text-sm font-medium text-gray-700"
                        >
                          <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                            <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#4285F4"/>
                            <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/>
                            <path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.997 8.997 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
                            <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
                          </svg>
                          Sign in with Google
                        </button>
                        
                        {showResendVerification && (
                          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                            <p className="text-sm text-yellow-800 mb-2">
                              Your email is not verified. Click below to resend the verification email.
                            </p>
                            <Button
                              type="button"
                              onClick={handleResendVerification}
                              variant="outline"
                              size="sm"
                              className="w-full"
                              disabled={loading}
                            >
                              {loading ? 'Sending...' : 'Resend Verification Email'}
                            </Button>
                          </div>
                        )}
                      </form>
                    </TabsContent>
                    
                    <TabsContent value="register">
                      <form onSubmit={handleRegister} className="space-y-4">
                        <div>
                          <Input
                            data-testid="register-username"
                            id="register-username"
                            name="nickname"
                            type="text"
                            autoComplete="nickname"
                            placeholder="Username (display name)"
                            value={registerForm.username}
                            onChange={(e) => setRegisterForm({ ...registerForm, username: e.target.value })}
                            required
                            className="bg-white border-cyan-500/30 placeholder:text-gray-400"
                            style={{ color: '#000000' }}
                          />
                        </div>
                        <div>
                          <Input
                            data-testid="register-fullname"
                            id="register-fullname"
                            name="name"
                            type="text"
                            autoComplete="name"
                            placeholder="Full Name"
                            value={registerForm.full_name}
                            onChange={(e) => setRegisterForm({ ...registerForm, full_name: e.target.value })}
                            required
                            className="bg-white border-cyan-500/30 placeholder:text-gray-400"
                            style={{ color: '#000000' }}
                          />
                        </div>
                        <div>
                          <Input
                            data-testid="register-email"
                            id="register-email"
                            name="email"
                            type="email"
                            autoComplete="username"
                            inputMode="email"
                            placeholder="Email (this is your login)"
                            value={registerForm.email}
                            onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                            required
                            className="bg-white border-cyan-500/30 placeholder:text-gray-400"
                            style={{ color: '#000000' }}
                          />
                        </div>
                        <div className="relative">
                          <Input
                            data-testid="register-password"
                            id="register-password"
                            name="new-password"
                            type={showRegisterPassword ? "text" : "password"}
                            autoComplete="new-password"
                            placeholder="Password (min 8 chars, 1 uppercase, 1 number)"
                            value={registerForm.password}
                            onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                            required
                            className="bg-white border-cyan-500/30 placeholder:text-gray-400 pr-10"
                            style={{ color: '#000000' }}
                          />
                          <button
                            type="button"
                            onClick={() => setShowRegisterPassword(!showRegisterPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-800"
                          >
                            {showRegisterPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>

                        {/* Legal Acceptances */}
                        <div className="space-y-3 border-t border-gray-700 pt-4">
                          <div className="flex items-start space-x-2">
                            <input
                              type="checkbox"
                              id="accept_terms"
                              checked={registerForm.accept_terms}
                              onChange={(e) => setRegisterForm({ ...registerForm, accept_terms: e.target.checked })}
                              required
                              className="mt-1 cursor-pointer"
                            />
                            <label htmlFor="accept_terms" className="text-sm text-gray-300 cursor-pointer">
                              I accept the{' '}
                              <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: '#2dd4bf' }} className="hover:underline">
                                Terms of Service
                              </a>
                            </label>
                          </div>

                          <div className="flex items-start space-x-2">
                            <input
                              type="checkbox"
                              id="accept_privacy"
                              checked={registerForm.accept_privacy}
                              onChange={(e) => setRegisterForm({ ...registerForm, accept_privacy: e.target.checked })}
                              required
                              className="mt-1 cursor-pointer"
                            />
                            <label htmlFor="accept_privacy" className="text-sm text-gray-300 cursor-pointer">
                              I accept the{' '}
                              <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#2dd4bf' }} className="hover:underline">
                                Privacy Policy
                              </a>
                            </label>
                          </div>

                          <div className="flex items-start space-x-2">
                            <input
                              type="checkbox"
                              id="accept_guidelines"
                              checked={registerForm.accept_guidelines}
                              onChange={(e) => setRegisterForm({ ...registerForm, accept_guidelines: e.target.checked })}
                              required
                              className="mt-1 cursor-pointer"
                            />
                            <label htmlFor="accept_guidelines" className="text-sm text-gray-300 cursor-pointer">
                              I accept the{' '}
                              <a href="/community-guidelines" target="_blank" rel="noopener noreferrer" style={{ color: '#2dd4bf' }} className="hover:underline">
                                Community Guidelines
                              </a>
                            </label>
                          </div>
                        </div>

                        <Button data-testid="register-submit-btn" type="submit" className="w-full btn-primary" disabled={loading}>
                          {loading ? 'Creating account...' : 'Create Account'}
                        </Button>

                        {/* Divider */}
                        <div className="relative my-2">
                          <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-gray-300" />
                          </div>
                          <div className="relative flex justify-center text-xs">
                            <span className="px-2 text-gray-500 bg-white">or</span>
                          </div>
                        </div>

                        {/* Google Sign-Up */}
                        <button
                          type="button"
                          onClick={handleGoogleSignIn}
                          data-testid="google-signup-btn"
                          className="w-full flex items-center justify-center gap-3 px-4 py-2.5 rounded-md border border-gray-300 hover:border-gray-400 bg-white hover:bg-gray-50 transition-all text-sm font-medium text-gray-700"
                        >
                          <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                            <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#4285F4"/>
                            <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/>
                            <path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.997 8.997 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
                            <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
                          </svg>
                          Sign up with Google
                        </button>
                      </form>
                    </TabsContent>
                  </Tabs>
                  )}
                  
                  <div className="text-center mt-6">
                    <Button 
                      variant="ghost" 
                      onClick={() => setShowAuth(false)}
                      className="text-cyan-400 hover:text-cyan-300"
                    >
                      ← Back to Home
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HomePage;